/**
 * AddOfferingSlideForm
 *
 * Slider-optimised replacement for AddOfferings + EachAddOffering inside a SlidePanel.
 * ALL logic is identical to EachAddOffering.jsx — only the layout is different
 * (full-width stacked fields instead of Col md={3} grid).
 *
 * EachAddOffering.jsx is NOT touched — this file exists solely for the SlidePanel.
 */
import React, { Fragment, useEffect, useRef, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { MdOutlineDeleteOutline } from "react-icons/md";
import * as apicall from "../../../components/Apicall";
import { ImagePicker } from "../../BrandingOffers/shared";

// ── helpers ──────────────────────────────────────────────────────────────────

const normalize = (val) => val?.toLowerCase().replace(/\s+/g, "").trim();

const convertToHoursMins = (mins) => {
  if (!mins && mins !== 0) return "";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  let s = "";
  if (h) s += `${h} hr `;
  if (m) s += `${m} min`;
  return s.trim() || "0 min";
};

const BUFFER_OPTIONS = ["0m", "30m", "60m", "90m"].map((t) => ({
  label: t,
  value: t,
}));

// ── tiny sub-components ───────────────────────────────────────────────────────

function Label({ children, tooltip }) {
  return (
    <label style={{ fontSize: "13px", fontWeight: 500, marginBottom: "4px", display: "block", color: "#374151" }}>
      {children}
      {tooltip && (
        <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
          <span style={{ marginLeft: "6px", cursor: "pointer" }}>
            <AiOutlineInfoCircle size={13} color="#9CA3AF" />
          </span>
        </OverlayTrigger>
      )}
    </label>
  );
}

function FieldError({ msg }) {
  if (!msg) return null;
  return <div className="invalid-feedback d-block" style={{ fontSize: "12px" }}>{msg}</div>;
}

const fieldWrap = { marginBottom: "16px" };

// ── single offering form ──────────────────────────────────────────────────────

function SingleOfferingForm({
  allServices,
  partners,
  data,
  updateData,
  updateOfferingData,
  index,
  removeOffering,
  isUpdate,
  showRemove,
  show,
}) {
  let allServicesData = localStorage.getItem("userDetails");
  if (allServicesData && allServicesData !== "undefined") {
    allServicesData = JSON.parse(allServicesData);
  }
  const isClinicService = allServicesData?.industry === 1;
  const isSportsService = allServicesData?.industry === 7;

  const [service, setService] = useState({});
  const [serviceType, setServiceType] = useState([]);
  const [allType, setAllType] = useState([]);
  const [allOffering, setAllOffering] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState(null);
  const lastRequestId = useRef(0);

  useEffect(() => {
    if (data?.service_id) getserviceTypes(data.service_id);
  }, [data?.service_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Default the Profile dropdown to the first available profile when adding a
  // new offering — SlidePanel keeps this form mounted even while hidden, so
  // this reacts to `show` (panel opening) rather than relying on mount timing.
  useEffect(() => {
    if (isUpdate || !show || data?.partner_id || !partners?.length) return;
    const first = partners[0];
    keyValueChanged("partner_id-partner_name", `${first.id}-${first.partner_name}`);
    setSelectedPartnerId(first.id);
    if (data?.service_type_id) {
      getAlloffering(`${data.service_type_id}-${data.service_type_name}`, first.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, partners, data?.partner_id]);

  // ── API helpers (identical to EachAddOffering) ──────────────────────────────

  const getserviceTypes = async (s1) => {
    try {
      const currentId = ++lastRequestId.current;
      const response = await apicall.get(`get_ezp_service_type?service_id=${s1}`);
      if (currentId !== lastRequestId.current) return;
      const ans = response?.data?.map((item) => ({
        service_type_id: item.id,
        service_type_name: item.name,
      })) || [];
      ans.unshift({ service_type_id: 0, service_type_name: "Custom" });
      setServiceType(ans);
    } catch (e) {
      console.log("err---", e);
    }
  };

  const getAlloffering = async (typeValue, partnerIdParam) => {
    try {
      const service_type_id = Number(typeValue?.split("-")[0]);
      const partner_id = partnerIdParam;
      if (!partner_id) return;
      // clear stale offerings so previous sub-category data doesn't linger
      setAllOffering([]);
      if (service_type_id === 0) {
        const response = await apicall.get(`getAllType/${service?.id}/${partner_id}`);
        setAllType(response?.data?.map((e) => e?.name));
      } else {
        const response = await apicall.get(`getAlloffering/${service_type_id}/${partner_id}`);
        setAllOffering(response?.data?.map((e) => e?.name));
      }
    } catch (e) {
      console.log("err---", e);
    }
  };

  // ── field update (identical logic to EachAddOffering) ────────────────────────

  const updateField = (field, value) => {
    try {
      let copyData = { ...data };
      if (field === "time_required") {
        let d1 = copyData[field];
        d1 = value === "+" ? d1 + 30 : d1 - 30;
        if (d1 < 30) d1 = 30;
        if (d1 > 480) d1 = 480;
        copyData[field] = d1;
      } else {
        copyData[field] = value;
      }
      if (copyData?.["error"]?.[`${field}_error`]) {
        delete copyData["error"][`${field}_error`];
      }
      if (field === "custom_type_name") {
        const norm = normalize(value);
        if (allType?.some((item) => item && normalize(String(item)) === norm)) {
          if (!copyData.error) copyData.error = {};
          copyData.error[`${field}_error`] = "Already this sub category is Existed";
        }
      }
      if (field === "service_offering_name") {
        const norm = normalize(value);
        if (allOffering?.some((item) => item && normalize(String(item)) === norm)) {
          if (!copyData.error) copyData.error = {};
          copyData.error[`${field}_error`] = "Already this Service type is Existed";
        }
      }
      // Duplicate checks are scoped by profile — a stale error from the previous
      // profile says nothing about the newly-selected one.
      if (field === "partner_id") {
        if (copyData?.error?.service_offering_name_error) {
          delete copyData.error.service_offering_name_error;
        }
        if (copyData?.error?.custom_type_name_error) {
          delete copyData.error.custom_type_name_error;
        }
      }
      if (!isUpdate) {
        updateData({ index, copyData });
      } else {
        updateOfferingData(copyData);
      }
    } catch (e) {
      console.log("errr---", e);
    }
  };

  const keyValueChanged = (k1, v1) => {
    try {
      const d1 = k1.split("-");
      const d2 = v1.split("-");
      let copyData = { ...data };
      copyData[d1[0]] = Number(d2[0]) || 0;
      copyData[d1[1]] = d2[1] || "";

      if (d1[0] === "service_id") {
        const s1 = allServices?.find((s) => s.id === Number(d2[0]));
        setService(s1 || {});
        copyData.error = {};
        copyData.service_type_id = 0;
        copyData.service_type_name = "";
        copyData.service_offering_id = 0;
        copyData.service_offering_name = "";
        copyData.amount = "";
        copyData.time_required = 30;
        copyData.buffer = null;
        if ("custom_type_name" in copyData) delete copyData.custom_type_name;
        if (s1?.service_type) {
          getserviceTypes(Number(d2[0]));
          copyData.service_type = true;
        } else {
          copyData.service_type = false;
        }
      }
      if (d1[0] === "service_type_id" && d2[1] === "Custom") {
        copyData.custom_type_name = "";
      } else if (d1[0] === "service_type_id") {
        if ("custom_type_name" in copyData) delete copyData.custom_type_name;
      }
      if (copyData?.["error"]?.[`${d1[1]}_error`]) {
        delete copyData["error"][`${d1[1]}_error`];
      }
      // when sub-category changes, clear any stale service offering duplicate error
      if (d1[0] === "service_type_id" && copyData?.error?.service_offering_name_error) {
        delete copyData.error.service_offering_name_error;
      }
      // Duplicate checks (both sub category and offering name) are scoped by
      // profile — a name flagged as already existing under the PREVIOUS profile
      // says nothing about the NEW one, so switching profiles must clear both,
      // not just leave the stale error sitting there.
      if (d1[0] === "partner_id") {
        if (copyData?.error?.service_offering_name_error) {
          delete copyData.error.service_offering_name_error;
        }
        if (copyData?.error?.custom_type_name_error) {
          delete copyData.error.custom_type_name_error;
        }
      }
      updateData({ index, copyData });
    } catch (e) {
      console.log("err---", e);
    }
  };

  // ── render ────────────────────────────────────────────────────────────────────

  const err = data?.error || {};

  return (
    <div
      style={{
        border: "1px solid #E5E7EB",
        borderRadius: "sa10px",
        padding: "16px",
        background: "#FAFAFA",
        position: "relative",
      }}
    >
      {/* Remove row button (add mode only, more than 1 row) */}
      {!isUpdate && showRemove && (
        <button
          onClick={() => removeOffering(index)}
          title="Remove"
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            background: "none",
            border: "1px solid #FECACA",
            borderRadius: "6px",
            color: "#DC2626",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            padding: "3px 6px",
          }}
        >
          <MdOutlineDeleteOutline size={15} />
        </button>
      )}

      {/* Profile */}
      <div style={fieldWrap}>
        <Label tooltip="Business or branch profile that manages its own services, pricing, and resources.">
          Profile <span style={{ color: "#DC2626" }}>*</span>
        </Label>
        {isUpdate ? (
          <Dropdown
            value={data?.partner_id || ""}
            options={partners?.map((t) => ({ label: t.partner_name, value: t.id }))}
            placeholder="Select Profile"
            onChange={(e) => updateField("partner_id", e.value)}
            className={`w-100 ${err.partner_name_error ? "is-invalid" : ""}`}
            style={{ width: "100%", borderColor: err.partner_name_error ? "#dc3545" : "#E9ECEF" }}
            appendTo="self"
          />
        ) : (
          <Dropdown
            value={data?.partner_id && data?.partner_name ? `${data.partner_id}-${data.partner_name}` : ""}
            options={partners?.map((t) => ({ label: t.partner_name, value: `${t.id}-${t.partner_name}` }))}
            placeholder="Select Profile"
            onChange={(e) => {
              keyValueChanged("partner_id-partner_name", e.value);
              const pid = Number(e.value.split("-")[0]);
              setSelectedPartnerId(pid);
              if (data?.service_type_id) {
                getAlloffering(`${data.service_type_id}-${data.service_type_name}`, pid);
              }
            }}
            className={`w-100 ${err.partner_name_error ? "is-invalid" : ""}`}
            style={{ width: "100%", borderColor: err.partner_name_error ? "#dc3545" : "#E9ECEF" }}
            appendTo="self"
          />
        )}
        <FieldError msg={err.partner_name_error} />
      </div>

      {/* Service Category */}
      <div style={fieldWrap}>
        <Label tooltip="Main grouping of services, such as General Cleaning or Pest Control.">
          {isClinicService ? "Specialization" : isSportsService ? "Sport" : "Service Category"} <span style={{ color: "#DC2626" }}>*</span>
        </Label>
        {isUpdate ? (
          <input
            type="text"
            className="form-control"
            value={data?.service_name || ""}
            onChange={(e) => updateField("service_name", e.target.value)}
            placeholder={isClinicService ? "Specialization" : isSportsService ? "Sport" : "Service Category"}
            style={{ borderColor: err.service_name_error ? "#dc3545" : "#E9ECEF" }}
          />
        ) : (
          <Dropdown
            value={data?.service_id ? `${data.service_id}-${data.service_name}` : ""}
            options={allServices?.map((s) => ({ label: s.name, value: `${s.id}-${s.name}` }))}
            placeholder={isClinicService ? "Select Specialization" : isSportsService ? "Select Sport" : "Select Service Category"}
            onChange={(e) => keyValueChanged("service_id-service_name", e.value)}
            className={`w-100 ${err.service_name_error ? "is-invalid" : ""}`}
            style={{ width: "100%", height: "40px", backgroundColor: "white", border: err.service_name_error ? "1px solid #dc3545" : "1px solid #E9ECEF" }}
            appendTo="self"
          />
        )}
        <FieldError msg={err.service_name_error} />
      </div>

      {/* Conditional fields — shown after a service is selected */}
      {(data?.service_name || isUpdate) && (
        <Fragment>
          {/* Sub Category — only for non-clinic services */}
          {!isClinicService && (
            <>
              {!isUpdate && Boolean(service?.service_type || data?.service_type || data?.service_type_id) && (
                <div style={fieldWrap}>
                  <Label tooltip="More specific classification within a service category, like Kitchen Cleaning or Bed Bug Treatment.">
                    Sub Category
                  </Label>
                  <Dropdown
                    value={data?.service_type_id || data?.service_type_name
                      ? `${data?.service_type_id}-${data?.service_type_name}`
                      : ""}
                    options={serviceType?.map((t) => ({ label: t.service_type_name, value: `${t.service_type_id}-${t.service_type_name}` }))}
                    placeholder="Select Sub Category"
                    onChange={(e) => {
                      keyValueChanged("service_type_id-service_type_name", e.value);
                      if (selectedPartnerId) getAlloffering(e.value, selectedPartnerId);
                    }}
                    className={`w-100 ${err.service_type_name_error ? "is-invalid" : ""}`}
                    style={{ width: "100%", borderColor: err.service_type_name_error ? "#dc3545" : "#E9ECEF" }}
                    appendTo="self"
                  />
                  <FieldError msg={err.service_type_name_error} />
                </div>
              )}

              {isUpdate && data?.service_type && (
                <div style={fieldWrap}>
                  <Label tooltip="More specific classification within a service category.">Sub Category</Label>
                  <input
                    type="text"
                    className="form-control"
                    value={data?.service_type_name || ""}
                    onChange={(e) => updateField("service_type_name", e.target.value)}
                    placeholder="Sub Category"
                    style={{ borderColor: err.service_type_name_error ? "#dc3545" : "#E9ECEF" }}
                  />
                  <FieldError msg={err.service_type_name_error} />
                </div>
              )}

              {/* Custom Sub Category */}
              {!isUpdate && data?.service_type_name === "Custom" && (
                <div style={fieldWrap}>
                  <Label tooltip="Custom sub-category name.">
                    Custom Sub Category <span style={{ color: "#DC2626" }}>*</span>
                  </Label>
                  <input
                    type="text"
                    className="form-control"
                    value={data?.custom_type_name && data.custom_type_name !== "0" ? data.custom_type_name : ""}
                    onChange={(e) => updateField("custom_type_name", e.target.value)}
                    placeholder="Enter custom sub-category name"
                    style={{ borderColor: err.custom_type_name_error ? "#dc3545" : "#E9ECEF" }}
                  />
                  <FieldError msg={err.custom_type_name_error} />
                </div>
              )}
            </>
          )}

          {/* Service Type (offering name) */}
          <div style={fieldWrap}>
            <Label tooltip="The specific service offering for which bookings, pricing, and duration are configured.">
              {isClinicService ? "Specialization Type" : isSportsService ? "Sports Type" : "Service Type"} <span style={{ color: "#DC2626" }}>*</span>
            </Label>
            <input
              type="text"
              className="form-control"
              value={data?.service_offering_name || ""}
              onChange={(e) => updateField("service_offering_name", e.target.value)}
              placeholder={isClinicService ? "Specialization Type" : isSportsService ? "Sports Type" : "Service Type"}
              style={{ borderColor: err.service_offering_name_error ? "#dc3545" : "#E9ECEF" }}
            />
            <FieldError msg={err.service_offering_name_error} />
          </div>

          {/* Optional icon for the service type (Upload an Icon), on add and edit. On edit the saved icon is shown until a new one is picked. */}
          <div style={fieldWrap}>
            <ImagePicker
              value={data?.service_offering_icon || (isUpdate ? data?.service_offering_image : null) || null}
              canRemove={Boolean(data?.service_offering_icon)}
              onChange={(img) => updateField("service_offering_icon", img)}
            />
          </div>

          {/* Price */}
          <div style={fieldWrap}>
            <Label tooltip="The amount charged to the customer for the selected service.">
              Price <span style={{ color: "#DC2626" }}>*</span>
            </Label>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#6B7280", fontSize: "13px", pointerEvents: "none" }}>
                AED
              </span>
              <input
                type="text"
                className="form-control"
                value={data?.amount || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  let updated = { ...data };
                  updated.amount = value;
                  if (value !== "" && Number(value) <= 0) {
                    updated.error = { ...updated.error, amount_error: "Price must be greater than 0" };
                  } else {
                    if (updated.error) delete updated.error.amount_error;
                  }
                  if (!isUpdate) {
                    updateData({ index, copyData: updated });
                  } else {
                    updateOfferingData(updated);
                  }
                }}
                placeholder="0.00"
                style={{ paddingLeft: "48px", borderColor: err.amount_error ? "#dc3545" : "#E9ECEF" }}
              />
            </div>
            <FieldError msg={err.amount_error} />
          </div>

          {/* Time Required + Buffer — two columns on wide screens, stacked on narrow */}
          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "16px" }}>
            {/* Time Required */}
            <div style={{ flex: "1 1 160px", minWidth: "140px" }}>
              <Label tooltip="Estimated duration needed to complete the service.">
                Time Required <span style={{ color: "#DC2626" }}>*</span>
              </Label>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid #E9ECEF",
                  borderRadius: "6px",
                  overflow: "hidden",
                  height: "40px",
                  background: "#fff",
                }}
              >
                <button
                  type="button"
                  onClick={() => updateField("time_required", "-")}
                  style={{ flex: "0 0 36px", height: "100%", border: "none", borderRight: "1px solid #E9ECEF", background: "#F9FAFB", cursor: "pointer", fontSize: "18px", color: "#374151", display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  −
                </button>
                <div style={{ flex: 1, textAlign: "center", fontSize: "13px", color: "#374151", fontWeight: 500 }}>
                  {convertToHoursMins(data?.time_required)}
                </div>
                <button
                  type="button"
                  onClick={() => updateField("time_required", "+")}
                  style={{ flex: "0 0 36px", height: "100%", border: "none", borderLeft: "1px solid #E9ECEF", background: "#F9FAFB", cursor: "pointer", fontSize: "18px", color: "#374151", display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  +
                </button>
              </div>
            </div>

            {/* Buffer */}
            <div style={{ flex: "1 1 160px", minWidth: "140px" }}>
              <Label tooltip="Additional time between bookings to allow for travel, preparation, or delays.">
                Buffer <span style={{ color: "#DC2626" }}>*</span>
              </Label>
              <Dropdown
                value={data?.buffer !== undefined && data?.buffer !== null ? `${data.buffer}m` : ""}
                options={BUFFER_OPTIONS}
                placeholder="Select Buffer"
                onChange={(e) => updateField("buffer", Number(e.value.replace("m", "")))}
                className={`w-100 ${err.buffer_error ? "is-invalid" : ""}`}
                style={{ width: "100%", height: "40px", borderColor: err.buffer_error ? "#dc3545" : "#E9ECEF" }}
                appendTo="self"
              />
              <FieldError msg={err.buffer_error} />
            </div>
          </div>
        </Fragment>
      )}
    </div>
  );
}

// ── outer wrapper (handles list + add-more for add mode) ──────────────────────

function AddOfferingSlideForm({
  allServices,
  partners,
  data,
  addMore,
  updateData,
  removeOffering,
  show,
  // update mode props
  isUpdate = false,
  singleData,
  updateOfferingData,
}) {
  let allServicesData = localStorage.getItem("userDetails");
  if (allServicesData && allServicesData !== "undefined") {
    allServicesData = JSON.parse(allServicesData);
  }
  const isSportsService = allServicesData?.industry === 7;

  if (isUpdate) {
    return (
      <SingleOfferingForm
        allServices={allServices}
        partners={partners}
        data={singleData}
        updateOfferingData={updateOfferingData}
        isUpdate
        index={0}
        removeOffering={() => {}}
        showRemove={false}
      />
    );
  }

  return (
    <div>
      {data?.map((ele, idx) => (
        <div key={idx} style={{ marginBottom: idx < data.length - 1 ? "16px" : 0 }}>
          {data.length > 1 && (
            <div style={{ fontSize: "11px", fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "8px" }}>
              Offering {idx + 1}
            </div>
          )}
          <SingleOfferingForm
            allServices={allServices}
            partners={partners}
            data={ele}
            updateData={updateData}
            index={idx}
            removeOffering={removeOffering}
            showRemove={data.length > 1}
            isUpdate={false}
            show={show}
          />
        </div>
      ))}

      <button
        onClick={addMore}
        style={{
          marginTop: "12px",
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          border: "1px dashed #C2185B",
          borderRadius: "8px",
          padding: "8px 16px",
          background: "transparent",
          color: "#C2185B",
          fontSize: "13px",
          fontWeight: 500,
          cursor: "pointer",
        }}
      >
        + Add Another {isSportsService ? "Sports Type" : "Offering"}
      </button>
    </div>
  );
}

export default AddOfferingSlideForm;
